import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Award, Briefcase, CalendarDays, ChevronDown, Clock, Globe2, Heart, Loader2,
  Megaphone, Newspaper, Package, PartyPopper, PenLine, Search, ShoppingBag, Sparkles, TrendingUp, User,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { usePageMeta } from "../components/service/ServiceBits";
import api, { apiError, API_BASE } from "../lib/api";

const CATEGORIES = ["All", "Company News", "Shipping Tips", "International Shipping", "Domestic Courier",
  "NRI Shipping", "eCommerce", "Business Logistics", "Packing Guides", "Events", "Achievements",
  "Announcements", "Industry Insights"];

const CAT_ICONS = { "Company News": Newspaper, "Shipping Tips": Sparkles, "International Shipping": Globe2,
  "Domestic Courier": Package, "NRI Shipping": Heart, "eCommerce": ShoppingBag, "Business Logistics": Briefcase,
  "Packing Guides": Package, "Events": PartyPopper, "Achievements": Award, "Announcements": Megaphone,
  "Industry Insights": TrendingUp };

export const articleImg = (a, size = "card") => {
  if (a.thumb_path && size === "thumb") return `${API_BASE}/api/files/${a.thumb_path}`;
  if (a.card_path && size === "card") return `${API_BASE}/api/files/${a.card_path}`;
  return a.image_path ? `${API_BASE}/api/files/${a.image_path}` : null;
};
const focal = (a) => ({ objectPosition: `${a.focal_x ?? 50}% ${a.focal_y ?? 50}%` });
const fmtDate = (a) => (a.published_date || (a.created_at || "").slice(0, 10));

const CAT_SECTIONS = [
  { cat: "Company News", title: "IndiaFast Updates", sub: "New services, locations, partnerships and milestones.", cta: null },
  { cat: "Shipping Tips", title: "Shipping Made Simple", sub: "Practical guides for packing, pricing and smooth deliveries.", cta: ["/pickup-request", "Plan a Shipment"] },
  { cat: "International Shipping", title: "Ship Beyond Borders", sub: "Customs, documentation, duties and global shipping know-how.", cta: ["/international-courier", "Explore International Courier"] },
  { cat: "NRI Shipping", title: "From India to Your Loved Ones Abroad", sub: "Food, gifts, festival parcels and personal shipments.", cta: ["/nri-courier", "Explore NRI Courier"] },
  { cat: "eCommerce", title: "eCommerce Shipping Insights", sub: "Shipping for D2C brands, sellers and growing online businesses.", cta: ["/ecommerce-shipping", "Explore eCommerce Shipping"] },
  { cat: "Business Logistics", title: "Business & Logistics Insights", sub: "Corporate courier, bulk shipments and logistics operations.", cta: ["/business-courier", "Explore Business Courier"] },
  { cat: "Packing Guides", title: "Learn How to Pack Better", sub: "Step-by-step packing guides for every kind of shipment.", cta: ["/media-videos", "View Packing Guides"] },
];

export function ArticleCard({ a, big, horizontal, testid }) {
  const img = articleImg(a, big ? "card" : "card");
  return (
    <Link to={`/p/news/${a.slug}`} data-testid={testid || `news-card-${a.id}`}
          className={`group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl ${horizontal ? "flex" : ""}`}>
      <div className={`relative overflow-hidden bg-navy ${horizontal ? "w-36 shrink-0 sm:w-44" : big ? "aspect-[16/10]" : "aspect-video"}`}>
        {img && <img src={img} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={focal(a)} />}
        <span className="absolute left-3 top-3 rounded-full bg-navy/85 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-saffron backdrop-blur">{a.category}</span>
      </div>
      <div className={horizontal ? "min-w-0 p-4" : "p-5"}>
        <h3 className={`line-clamp-2 font-heading font-bold text-navy transition-colors group-hover:text-saffron ${big ? "text-xl" : "text-base"}`}>{a.title}</h3>
        {a.excerpt && <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-slate-500">{a.excerpt}</p>}
        <p className="mt-3 flex flex-wrap items-center gap-x-2 text-[10px] font-semibold text-slate-400">
          <span className="flex items-center gap-1"><User className="h-3 w-3" />{a.author}</span>
          <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{fmtDate(a)}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{a.reading_time} min read</span>
        </p>
      </div>
    </Link>
  );
}

export default function NewsHub() {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [popular, setPopular] = useState([]);
  const [picks, setPicks] = useState([]);
  const [contact, setContact] = useState(null);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [moreLoading, setMoreLoading] = useState(false);
  const [sub, setSub] = useState({ name: "", email: "" });
  const [subBusy, setSubBusy] = useState(false);
  const LIMIT = 12;

  usePageMeta("IndiaFast News & Blog | Courier, Shipping & Logistics Insights",
    "Read the latest IndiaFast news, shipping tips, international courier guides, NRI shipping insights, eCommerce logistics updates and company stories.", "/p/news");

  const load = useCallback(async (s = 0, append = false) => {
    append ? setMoreLoading(true) : setLoading(true);
    try {
      const p = new URLSearchParams({ limit: LIMIT, skip: s });
      if (cat !== "All") p.set("category", cat);
      if (q) p.set("q", q);
      const r = await api.get(`/news/public?${p}`);
      setArticles((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
      setTotal(r.data.total);
      setSkip(s + r.data.items.length);
    } catch {} finally { setLoading(false); setMoreLoading(false); }
  }, [cat, q]);

  useEffect(() => { load(0, false); }, [load]);
  useEffect(() => {
    api.get("/news/public/popular").then((r) => setPopular(r.data)).catch(() => {});
    api.get("/news/public?editors_pick=1&limit=3").then((r) => setPicks(r.data.items)).catch(() => {});
    api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {});
  }, []);

  const isFiltered = cat !== "All" || q;
  const featured = useMemo(() => articles.find((a) => a.featured) || articles[0], [articles]);
  const rest = useMemo(() => (isFiltered ? articles : articles.filter((a) => a !== featured)), [articles, featured, isFiltered]);

  const subscribe = async (e) => {
    e.preventDefault();
    setSubBusy(true);
    try { const r = await api.post("/news/public/subscribe", sub); toast.success(r.data.message); setSub({ name: "", email: "" }); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSubBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="news-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-navy pb-20 pt-32 sm:pt-36" data-testid="news-hero">
        <div className="pointer-events-none absolute -right-24 top-0 h-80 w-80 rounded-full bg-saffron/15 blur-3xl" />
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1.5" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full opacity-20" aria-hidden="true">
          <line x1="10%" y1="65%" x2="40%" y2="35%" stroke="#f97316" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="40%" y1="35%" x2="70%" y2="60%" stroke="#ffffff" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
          <line x1="70%" y1="60%" x2="90%" y2="30%" stroke="#16a34a" strokeWidth="1" strokeDasharray="4 6" className="network-dash" />
        </svg>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
          <p className="animate-fade-up flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-saffron"><Newspaper className="h-4 w-4" /> Newsroom</p>
          <h1 className="animate-fade-up stagger-1 mt-3 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            IndiaFast News &amp; <span className="text-saffron">Insights</span>
          </h1>
          <p className="animate-fade-up stagger-2 mt-4 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
            Shipping stories, logistics insights, company updates and practical guides from IndiaFast Global Couriers.
          </p>
        </div>
      </section>

      {/* CATEGORY NAV */}
      <div className="sticky top-16 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-xl" data-testid="news-catnav">
        <div className="mx-auto flex max-w-7xl items-center gap-2 overflow-x-auto px-4 py-3 sm:px-6">
          {CATEGORIES.map((c) => {
            const Icon = CAT_ICONS[c] || Newspaper;
            return (
              <button key={c} onClick={() => setCat(c)} data-testid={`news-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-all ${cat === c ? "bg-navy text-saffron shadow-lg" : "bg-slate-100 text-slate-600 hover:bg-saffron/10 hover:text-saffron"}`}>
                <Icon className="h-3.5 w-3.5" /> {c}
              </button>
            );
          })}
        </div>
      </div>

      {/* SEARCH */}
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6">
        <form onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); setSkip(0); }}
              className="flex max-w-xl items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-3 shadow-sm focus-within:border-saffron" data-testid="news-search">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="news-search-input"
                 placeholder="Search articles, shipping guides, news…" className="w-full bg-transparent text-sm text-navy outline-none placeholder:text-slate-400" />
          {q && <button type="button" onClick={() => { setQ(""); setQInput(""); }} className="text-xs font-bold text-saffron">Clear</button>}
        </form>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-slate-400" data-testid="news-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading stories…</div>
        ) : !articles.length ? (
          <div className="rounded-3xl border border-slate-200 bg-white py-24 text-center shadow-sm" data-testid="news-empty">
            <Newspaper className="mx-auto h-10 w-10 text-saffron/50" />
            <p className="mt-4 font-heading text-xl font-bold text-navy">No Stories Here Yet</p>
            <p className="mt-1 text-sm text-slate-500">New IndiaFast stories are coming soon.</p>
          </div>
        ) : (
          <>
            {/* FEATURED */}
            {!isFiltered && featured && (
              <section className="mb-14" data-testid="news-featured">
                <Link to={`/p/news/${featured.slug}`} className="group grid overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl transition-all hover:-translate-y-1 lg:grid-cols-2">
                  <div className="relative aspect-[16/10] overflow-hidden bg-navy lg:aspect-auto lg:min-h-[380px]">
                    {articleImg(featured, "card") && <img src={articleImg(featured, "card")} alt={featured.title} className="absolute h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={focal(featured)} />}
                  </div>
                  <div className="p-7 sm:p-10">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-saffron px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-white">Featured</span>
                      <span className="rounded-full bg-saffron/10 px-3 py-1 text-[9px] font-bold uppercase tracking-wider text-saffron">{featured.category}</span>
                    </div>
                    <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-navy transition-colors group-hover:text-saffron sm:text-3xl">{featured.title}</h2>
                    {featured.excerpt && <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-slate-500">{featured.excerpt}</p>}
                    <p className="mt-5 flex flex-wrap items-center gap-x-3 text-[11px] font-semibold text-slate-400">
                      <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{featured.author}</span>
                      <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(featured)}</span>
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{featured.reading_time} min read</span>
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors group-hover:bg-saffron-hover">
                      Read Story <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </section>
            )}

            {/* LATEST GRID */}
            <section data-testid="news-latest">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">{isFiltered ? (cat !== "All" ? cat : "Search Results") : "Latest"}</p>
                  <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{isFiltered ? `${total} article${total !== 1 ? "s" : ""}` : "Latest From IndiaFast"}</h2>
                </div>
              </div>
              <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((a, i) => <ArticleCard key={a.id} a={a} big={i === 0 && !isFiltered} horizontal={i > 3 && !isFiltered} />)}
              </div>
              {articles.length < total && (
                <div className="mt-10 text-center">
                  <button onClick={() => load(skip, true)} disabled={moreLoading} data-testid="news-load-more"
                          className="rounded-full border border-slate-300 bg-white px-8 py-3 text-sm font-bold text-navy shadow-sm transition-colors hover:border-saffron hover:text-saffron disabled:opacity-60">
                    {moreLoading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : `Load More (${total - articles.length} more)`}
                  </button>
                </div>
              )}
            </section>
          </>
        )}

        {/* CATEGORY INSIGHT SECTIONS (unfiltered view only) */}
        {!isFiltered && CAT_SECTIONS.map((sec) => {
          const list = articles.filter((a) => a.category === sec.cat).slice(0, 3);
          if (!list.length) return null;
          const Icon = CAT_ICONS[sec.cat] || Newspaper;
          return (
            <section key={sec.cat} className="mt-16" data-testid={`news-section-${sec.cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-saffron"><Icon className="h-4 w-4" /> {sec.cat}</p>
                  <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-navy sm:text-3xl">{sec.title}</h2>
                  <p className="mt-1 text-sm text-slate-500">{sec.sub}</p>
                </div>
                {sec.cta && (
                  <Link to={sec.cta[0]} data-testid={`news-section-cta-${sec.cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="group flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 text-xs font-bold text-navy transition-colors hover:border-saffron hover:text-saffron">
                    {sec.cta[1]} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
              <div className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((a) => <ArticleCard key={a.id} a={a} />)}
              </div>
            </section>
          );
        })}

        {/* MOST READ + EDITOR'S PICKS */}
        {!isFiltered && (popular.length > 0 || picks.length > 0) && (
          <section className="mt-16 grid gap-10 lg:grid-cols-2">
            {popular.length > 0 && (
              <div data-testid="news-popular">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Most Read</p>
                <h2 className="mt-2 font-heading text-xl font-extrabold text-navy sm:text-2xl">Popular Right Now</h2>
                <div className="mt-6 space-y-3">
                  {popular.filter((p) => p.views > 0).map((a, i) => (
                    <Link key={a.id} to={`/p/news/${a.slug}`} data-testid={`news-popular-${a.id}`}
                          className="group flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-lg">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy font-heading text-sm font-extrabold text-saffron">{i + 1}</span>
                      {articleImg(a, "thumb") && <img src={articleImg(a, "thumb")} alt="" loading="lazy" className="h-14 w-20 shrink-0 rounded-lg object-cover" style={focal(a)} />}
                      <div className="min-w-0">
                        <p className="line-clamp-1 text-sm font-bold text-navy group-hover:text-saffron">{a.title}</p>
                        <p className="mt-0.5 text-[10px] font-semibold text-slate-400">{a.category} · {a.views} reads</p>
                      </div>
                    </Link>
                  ))}
                  {!popular.some((p) => p.views > 0) && <p className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-xs text-slate-400">Reader rankings appear as articles are read.</p>}
                </div>
              </div>
            )}
            {picks.length > 0 && (
              <div data-testid="news-picks">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Editor's Picks</p>
                <h2 className="mt-2 font-heading text-xl font-extrabold text-navy sm:text-2xl">Handpicked by Our Team</h2>
                <div className="mt-6 space-y-3">
                  {picks.map((a) => (
                    <Link key={a.id} to={`/p/news/${a.slug}`} data-testid={`news-pick-${a.id}`}
                          className="group flex items-center gap-4 overflow-hidden rounded-2xl bg-navy p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl">
                      {articleImg(a, "thumb") && <img src={articleImg(a, "thumb")} alt="" loading="lazy" className="h-16 w-24 shrink-0 rounded-lg object-cover" style={focal(a)} />}
                      <div className="min-w-0">
                        <span className="rounded-full bg-saffron/15 px-2 py-0.5 text-[9px] font-bold uppercase text-saffron">{a.category}</span>
                        <p className="mt-1 line-clamp-2 text-sm font-bold text-white group-hover:text-saffron">{a.title}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* NEWSLETTER */}
        <section className="mt-16" data-testid="news-newsletter">
          <div className="overflow-hidden rounded-3xl bg-navy p-8 text-center sm:p-12">
            <div className="tricolor-beam h-1" />
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-saffron">Stay Updated</p>
            <h2 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Get IndiaFast Insights in Your Inbox</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-300">Shipping insights, company news and useful courier tips.</p>
            <form onSubmit={subscribe} className="mx-auto mt-6 flex max-w-md flex-col gap-2.5 sm:flex-row">
              <input required className="flex-1 rounded-full border border-white/15 bg-white/10 px-5 py-3 text-sm text-white outline-none backdrop-blur placeholder:text-slate-400 focus:border-saffron"
                     data-testid="newsletter-name" placeholder="Name" value={sub.name} onChange={(e) => setSub({ ...sub, name: e.target.value })} />
              <input required type="email" className="flex-1 rounded-full border border-white/15 bg-white/10 px-5 py-3 text-sm text-white outline-none backdrop-blur placeholder:text-slate-400 focus:border-saffron"
                     data-testid="newsletter-email" placeholder="Email" value={sub.email} onChange={(e) => setSub({ ...sub, email: e.target.value })} />
              <button disabled={subBusy} data-testid="newsletter-subscribe"
                      className="rounded-full bg-saffron px-7 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 transition-colors hover:bg-saffron-hover disabled:opacity-60">
                {subBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
              </button>
            </form>
          </div>
        </section>
      </main>

      {/* EDITORIAL CTA */}
      <section className="relative overflow-hidden bg-navy py-16 sm:py-20" data-testid="news-final-cta">
        <div className="tricolor-beam absolute inset-x-0 top-0 h-1" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-emerald-600/15 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">Stay Connected With IndiaFast</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-slate-300 sm:text-base">Follow our journey, discover useful shipping tips and stay updated with the latest from IndiaFast.</p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/media-videos" data-testid="news-cta-media" className="rounded-full bg-saffron px-7 py-3 text-sm font-bold text-white shadow-lg shadow-saffron/30 hover:bg-saffron-hover">Explore Media &amp; Videos</Link>
            <Link to="/rate-calculator" data-testid="news-cta-quote" className="rounded-full border border-white/25 px-7 py-3 text-sm font-bold text-white hover:bg-white/10">Get a Quote</Link>
            <Link to="/track" data-testid="news-cta-track" className="rounded-full border border-white/25 px-7 py-3 text-sm font-bold text-white hover:bg-white/10">Track Shipment</Link>
          </div>
        </div>
      </section>

      <Footer contact={contact} />
    </div>
  );
}
